import { UseBoundStore } from 'zustand';

/**
 * Persists store state during HMR
 *
 * During HMR, the store state is persisted to prevent loss of state when a module is reloaded.
 *
 * While developing some stores may require state to be persisted during HMR to avoid unexpected behavior.
 * This is an issue directly related to how Zustand performs with Vite's HMR.
 *
 * Example usage: the SessionGuard is dependent on the userStore to determine if a user is authenticated. When the
 * userStore is reset during HMR, the SessionGuard will not have access to the user state and will redirect the application
 * to the sign-out page. This behavior is not ideal during development.
 *
 * Related issues:
 * - https://github.com/pmndrs/zustand/issues/934
 * - https://github.com/pmndrs/zustand/discussions/827
 *
 * @param storeKey - Unique key that identifies the persisted store state
 * @param store - Store of which the state will be persisted
 */
export const persistStoreDuringHMR = (
  storeKey: string,
  store: UseBoundStore<any>,
): void => {
  if (
    !store.persist && // store does not already have persistence
    import.meta.hot && // Vite HMR is enabled
    import.meta.env.VITE_APP_ENV === 'local' // only allow in local development
  ) {
    const state = import.meta.hot.data[storeKey];

    if (state) {
      store.setState(import.meta.hot.data[storeKey]);
    }

    store.subscribe((nextState: any): void => {
      if (import.meta.hot) {
        import.meta.hot.data[storeKey] = nextState;
      }
    });

    // https://vitejs.dev/guide/api-hmr#hot-accept-cb
    import.meta.hot.accept((newModule): void => {
      if (import.meta.hot && newModule) {
        store.setState(import.meta.hot.data[storeKey]);
      }
    });
  }
};
